// External library imports
import { Link, useRouterState } from "@tanstack/react-router";
import { Bell, ChevronRight, Search } from "lucide-react";
import { useTranslation } from "react-i18next";

// Internal UI component imports
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger } from "@/components/ui/sidebar";
import { ModeToggle } from "@/components/mode-toggle";

/**
 * UUID regex pattern for filtering breadcrumb paths
 */
const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Interface for breadcrumb items
 */
interface BreadcrumbItem {
  label: string;
  path: string;
  isLast: boolean;
}

/**
 * AppHeader Component
 * Renders the application's main header with navigation breadcrumbs and action buttons
 */
export function AppHeader() {
  // Get current pathname from router state
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { t } = useTranslation();

  /**
   * Generates breadcrumb items from the current path
   * @param path - Current URL path
   * @returns Array of breadcrumb items
   */
  const getBreadcrumbs = (path: string): BreadcrumbItem[] => {
    const parts = path.split("/").filter(Boolean);
    
    // Check if this is an invoice route
    const isInvoiceEdit = parts.includes("invoices") && parts.includes("edit");
    const isInvoiceNew = parts.includes("invoices") && parts.includes("new");
    const invoiceId = isInvoiceEdit ? parts[parts.indexOf("edit") + 1] : null;
    
    // Custom breadcrumbs for invoice edit/view route
    if (isInvoiceEdit && invoiceId && UUID_PATTERN.test(invoiceId)) {
      return [
        {
          label: t('breadcrumb.dashboard'),
          path: "/dashboard",
          isLast: false,
        },
        {
          label: t('breadcrumb.invoices'),
          path: "/dashboard/invoices",
          isLast: false,
        },
        {
          label: t('breadcrumb.viewInvoice'),
          path: `/dashboard/invoices/edit/${invoiceId}`,
          isLast: true,
        },
      ];
    }
    
    // Custom breadcrumbs for invoice creation route
    if (isInvoiceNew) {
      return [
        {
          label: t('breadcrumb.dashboard'),
          path: "/dashboard",
          isLast: false,
        },
        {
          label: t('breadcrumb.invoices'),
          path: "/dashboard/invoices",
          isLast: false,
        },
        {
          label: t('breadcrumb.newInvoice'),
          path: "/dashboard/invoices/new",
          isLast: true,
        },
      ];
    }

    // Filter out UUIDs and 'edit' from path parts for default breadcrumbs
    const filteredParts = parts.filter((part) => !UUID_PATTERN.test(part) && part !== "edit");

    return filteredParts.map((part, index) => {
      // Try to get translation from breadcrumb keys, fallback to formatted part
      const key = part.toLowerCase().replace(/-/g, '');
      const translationKey = `breadcrumb.${key}`;
      const label = t(translationKey, { defaultValue: part.charAt(0).toUpperCase() + part.slice(1).replace(/-/g, " ") });
      
      return {
        label,
        path: "/" + filteredParts.slice(0, index + 1).join("/"),
        isLast: index === filteredParts.length - 1,
      };
    });
  };

  const breadcrumbs = getBreadcrumbs(pathname);

  return (
    <header
      className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-2 border-b bg-background px-3 transition-[width,height] ease-linear md:h-16 md:px-4"
      role="banner"
    >
      {/* Left section: Sidebar trigger and Breadcrumbs */}
      <div className="flex flex-1 items-center gap-2 overflow-hidden">
        <SidebarTrigger className="-ml-1 md:ml-0" aria-label="Toggle sidebar" />
        <Separator
          orientation="vertical"
          className="mr-2 hidden h-4 md:block"
          decorative
        />
        <nav
          aria-label="Breadcrumb navigation"
          className="hidden min-w-0 flex-1 sm:block"
        >
          <Breadcrumb>
            <BreadcrumbList className="flex-nowrap">
              {breadcrumbs.map((crumb) => (
                <BreadcrumbItem key={crumb.path} className="flex items-center">
                  {!crumb.isLast ? (
                    <>
                      <BreadcrumbLink asChild>
                        <Link
                          className="whitespace-nowrap text-sm capitalize transition-colors hover:text-primary"
                          to={crumb.path}
                        >
                          {crumb.label}
                        </Link>
                      </BreadcrumbLink>
                      <ChevronRight className="mx-1 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                    </>
                  ) : (
                    <BreadcrumbPage className="max-w-[120px] truncate text-sm font-medium capitalize md:max-w-[200px]">
                      {crumb.label}
                    </BreadcrumbPage>
                  )}
                </BreadcrumbItem>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </nav>
      </div>

      {/* Right section: Action buttons */}
      <div className="flex items-center gap-1 md:gap-2">
        <Button
          variant="ghost"
          size="icon"
          className="hidden h-8 w-8 hover:bg-muted sm:flex md:h-9 md:w-9"
          aria-label="Search"
        >
          <Search className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="hidden h-8 w-8 hover:bg-muted sm:flex md:h-9 md:w-9"
          aria-label="Notifications"
        >
          <Bell className="h-4 w-4" aria-hidden="true" />
        </Button>
        <ModeToggle />
      </div>
    </header>
  );
}
